/** Helpers for the RRM Channel Survey map — ACS flattening, filters, colors. */

import {
  attachLabelPositions,
  getDeviceGps,
  getDeviceType,
  isJnet2CapOrNode,
  normalizeBand,
  offsetLatLng,
} from './channelMapUtils';
import { buildDeviceMacIndex, normalizeMac } from './fieldSurveyLampCoverage';

/** Matches backend algo.ACSSurveyDefaultMaxAge (12 h). */
export const ACS_SURVEY_MAX_AGE_MS = 12 * 60 * 60 * 1000;
/** Matches backend algo.ACSEvilTwinDefaultMaxAge (24 h). */
export const EVIL_TWIN_SURVEY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const EVIL_TWIN_SURVEY_MAX_AGE_HOURS = EVIL_TWIN_SURVEY_MAX_AGE_MS / (60 * 60 * 1000);
export const DEFAULT_ACS_RSSI_FLOOR_DBM = -80;
export const SURVEY_MAP_MARKER_RADIUS_M = 28;

/** Which marker types to plot on the channel survey map. */
export const SURVEY_MARKER_SOURCE = {
  ALL: 'all',
  JUGANU: 'juganu',
  EXTERNAL: 'external',
};

export function normalizeSurveyMarkerSourceFilter(raw) {
  if (raw === SURVEY_MARKER_SOURCE.JUGANU || raw === SURVEY_MARKER_SOURCE.EXTERNAL) {
    return raw;
  }
  return SURVEY_MARKER_SOURCE.ALL;
}

export function isBackhaulSurveyBand(band) {
  const b = normalizeBand(band);
  return b === '5GHz' || b === '6GHz';
}

export function isSurveyEligibleDevice(device) {
  return Boolean(device?.serial_number)
    && isJnet2CapOrNode(device)
    && device.connected !== false;
}

export function parseSurveyReceivedAt(receivedAt) {
  if (!receivedAt) return null;
  const ms = Date.parse(receivedAt);
  return Number.isFinite(ms) ? ms : null;
}

export function isSurveyFresh(receivedAt, nowMs = Date.now(), maxAgeMs = ACS_SURVEY_MAX_AGE_MS) {
  const ts = parseSurveyReceivedAt(receivedAt);
  if (ts == null) return false;
  return nowMs - ts <= maxAgeMs;
}

function acsInt(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return 0;
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function normalizeChannelEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const channel = acsInt(raw, 'channel', 'Channel');
  if (channel <= 0) return null;
  const neighbours = raw.neighbours || raw.Neighbours || [];
  return {
    channel,
    frequency: acsInt(raw, 'frequency', 'Frequency'),
    maxRssi: acsInt(raw, 'max_rssi', 'maxrssi', 'MaxRSSI'),
    minRssi: acsInt(raw, 'min_rssi', 'minrssi', 'MinRSSI'),
    noiseFloor: acsInt(raw, 'noise_floor', 'NF', 'nf'),
    chLoad: acsInt(raw, 'ch_load', 'Ch_load'),
    bss: acsInt(raw, 'bss', 'BSS'),
    neighbours: Array.isArray(neighbours) ? neighbours : [],
  };
}

/** Derive dBm for an ACS neighbour row using the parent channel noise floor. */
export function deriveNeighbourDbm(neighbour, noiseFloor) {
  if (!neighbour || typeof neighbour !== 'object') return null;
  const snrOrRssi = acsInt(neighbour, 'RSSI', 'rssi');
  const nf = Number(noiseFloor);
  if (!Number.isFinite(nf) || nf >= -50 || nf <= -110 || nf === 0) return null;
  if (snrOrRssi === 0) return null;
  return snrOrRssi + nf;
}

/** Keep only neighbour BSSIDs heard at or above the minimum dBm floor. */
export function filterNeighboursByMinRssi(neighbours, noiseFloor, minRssiDbm) {
  const floor = Number(minRssiDbm);
  if (!Number.isFinite(floor)) return neighbours || [];
  return (neighbours || []).filter((n) => {
    const dbm = deriveNeighbourDbm(n, noiseFloor);
    return dbm != null && dbm >= floor;
  });
}

function channelInterferenceScore(entry) {
  const load = Math.max(0, Math.min(100, Number(entry?.chLoad) || 0));
  const maxRssi = Number(entry?.maxRssi);
  let score = load;
  if (Number.isFinite(maxRssi) && maxRssi < 0) {
    score += Math.max(0, (maxRssi + 100) * 0.8);
  }
  score += Math.min(30, (Number(entry?.bss) || 0) * 2);
  return score;
}

/** Stable green→red color from channel load and strongest foreign RSSI. */
export function getSurveyInterferenceColor(chLoad, maxRssi) {
  const load = Math.max(0, Math.min(100, Number(chLoad) || 0));
  let hue = 120 - load * 1.15;
  const rssi = Number(maxRssi);
  if (Number.isFinite(rssi) && rssi < 0 && rssi >= -65) {
    hue = Math.min(hue, 20);
  }
  hue = Math.max(0, Math.min(120, hue));
  return `hsl(${hue}, 72%, 42%)`;
}

export function collectSurveyChannelsFromDevices(devices, bandFilter = 'all') {
  const channelsByBand = { '5GHz': new Set(), '6GHz': new Set() };
  (devices || []).forEach((device) => {
    if (!isSurveyEligibleDevice(device)) return;
    const survey = device?.acs_survey;
    if (!survey?.radios) return;
    survey.radios.forEach((radio) => {
      const band = normalizeBand(radio?.band);
      if (!isBackhaulSurveyBand(band)) return;
      if (bandFilter !== 'all' && band !== bandFilter) return;
      (radio.channels || []).forEach((raw) => {
        const entry = normalizeChannelEntry(raw);
        if (entry) channelsByBand[band]?.add(entry.channel);
      });
    });
  });
  return {
    '5GHz': [...channelsByBand['5GHz']].sort((a, b) => a - b),
    '6GHz': [...channelsByBand['6GHz']].sort((a, b) => a - b),
  };
}

function neighbourBssid(neighbour) {
  return normalizeMac(
    neighbour?.BSSID
    || neighbour?.bssid
    || neighbour?.Mac
    || neighbour?.mac,
  );
}

function neighbourSsid(neighbour) {
  const ssid = String(
    neighbour?.Neighbour_ssid
    || neighbour?.neighbour_ssid
    || neighbour?.SSID
    || neighbour?.ssid
    || '',
  ).trim();
  return ssid || '—';
}

function ssidLabel(ssid) {
  if (!ssid || ssid === '—') return 'external';
  return ssid.length > 14 ? `${ssid.slice(0, 13)}…` : ssid;
}

/** Markers for non-fleet APs heard in ACS neighbour lists (third-party / external). */
function buildForeignHeardApMarkers(devices, options, macIndex) {
  const minRssiDbm = Number.isFinite(Number(options.minRssiDbm))
    ? Number(options.minRssiDbm)
    : DEFAULT_ACS_RSSI_FLOOR_DBM;
  const bandFilter = options.bandFilter || 'all';
  const selectedChannels = Array.isArray(options.selectedChannels) && options.selectedChannels.length > 0
    ? new Set(options.selectedChannels.map((c) => Number(c)).filter((c) => c > 0))
    : null;
  const nowMs = Number(options.nowMs) || Date.now();
  const markerSource = normalizeSurveyMarkerSourceFilter(options.markerSourceFilter);
  const showExternal = markerSource === SURVEY_MARKER_SOURCE.ALL
    || markerSource === SURVEY_MARKER_SOURCE.EXTERNAL;

  if (!showExternal) return [];

  const markers = [];
  const seen = new Set();

  (devices || []).forEach((device) => {
    if (!isSurveyEligibleDevice(device)) return;
    const survey = device?.acs_survey;
    if (!survey?.radios?.length) return;

    const fresh = isSurveyFresh(survey.received_at, nowMs);

    const hearerGps = getDeviceGps(device);
    if (!hearerGps) return;

    survey.radios.forEach((radio) => {
      const band = normalizeBand(radio?.band);
      if (!isBackhaulSurveyBand(band)) return;
      if (bandFilter !== 'all' && band !== bandFilter) return;

      (radio.channels || []).forEach((raw) => {
        const entry = normalizeChannelEntry(raw);
        if (!entry) return;
        if (selectedChannels && !selectedChannels.has(entry.channel)) return;

        const filteredNeighbours = filterNeighboursByMinRssi(
          entry.neighbours,
          entry.noiseFloor,
          minRssiDbm,
        );

        filteredNeighbours.forEach((neighbour, idx) => {
          const bssid = neighbourBssid(neighbour);
          if (bssid.length !== 12) return;
          // Fleet APs already appear via their own survey marker at device GPS.
          if (macIndex?.has(bssid)) return;

          const dedupeKey = `${bssid}|${entry.channel}|${device.serial_number}`;
          if (seen.has(dedupeKey)) return;
          seen.add(dedupeKey);

          const dbm = deriveNeighbourDbm(neighbour, entry.noiseFloor);
          const ssid = neighbourSsid(neighbour);
          const angleRad = ((parseInt(bssid.slice(0, 4), 16) + entry.channel + idx) % 360) * (Math.PI / 180);
          const offset = offsetLatLng(hearerGps.lat, hearerGps.lng, 10 + (idx % 4) * 3, angleRad);

          markers.push({
            id: `foreign|${device.serial_number.toLowerCase()}|${band}|${entry.channel}|${bssid}`,
            isForeign: true,
            serial: device.serial_number,
            heardBySerial: device.serial_number,
            bssid,
            ssid,
            ssidLabel: ssidLabel(ssid),
            lat: offset.lat,
            lng: offset.lng,
            hasGps: true,
            band,
            radioLabel: radio.label || '',
            channel: entry.channel,
            frequencyMHz: entry.frequency || null,
            rssiDbm: dbm,
            noiseFloor: entry.noiseFloor || null,
            chLoad: entry.chLoad || 0,
            receivedAt: survey.received_at || null,
            surveySource: survey.source || '',
            isFresh: fresh,
            isCap: false,
          });
        });
      });
    });
  });

  return markers;
}

function normalizeBssidKey(raw) {
  return normalizeMac(raw || '');
}

/** Plot evil-twin alerts from the backend analysis API near hearer GPS. */
export function buildEvilTwinMarkers(analysis, devices) {
  const alerts = analysis?.alerts;
  if (!Array.isArray(alerts) || alerts.length === 0) {
    return { markers: [], unplottable: [] };
  }

  const gpsBySerial = new Map();
  (devices || []).forEach((device) => {
    const serial = String(device?.serial_number || '').toLowerCase();
    if (!serial) return;
    const gps = getDeviceGps(device);
    if (gps) gpsBySerial.set(serial, gps);
  });

  const markers = [];
  const unplottable = [];
  const nowMs = Date.now();

  alerts.forEach((alert) => {
    const bssidKey = normalizeBssidKey(alert?.foreign_bssid);
    if (bssidKey.length !== 12) return;

    const hearings = Array.isArray(alert.hearings) ? [...alert.hearings] : [];
    if (hearings.length === 0) return;
    hearings.sort((a, b) => (Number(b.rssi_dbm) || -999) - (Number(a.rssi_dbm) || -999));
    const strongest = hearings[0];
    const hearerSerial = String(strongest?.hearer_serial || '').toLowerCase();
    const hearerGps = gpsBySerial.get(hearerSerial);
    if (!hearerGps) {
      unplottable.push(alert);
      return;
    }

    const channel = Number(alert.channel) || Number(strongest.channel) || 0;
    const band = alert.band || strongest.band || '';
    const ssid = alert.ssid || '—';
    const angleRad = ((parseInt(bssidKey.slice(0, 4), 16) + channel) % 360) * (Math.PI / 180);
    const offset = offsetLatLng(hearerGps.lat, hearerGps.lng, 14, angleRad);
    const receivedAt = strongest.survey_received_at || null;

    markers.push({
      id: `evil-twin|${bssidKey}|${channel}`,
      isEvilTwin: true,
      isForeign: false,
      ssid,
      ssidLabel: ssidLabel(ssid),
      bssid: bssidKey,
      foreignBssid: alert.foreign_bssid || formatMacColon(bssidKey),
      expectedOwnerSerials: alert.expected_owner_serials || [],
      hearerCount: alert.hearer_count || distinctHearerSerials(hearings).length,
      heardBySerial: hearerSerial,
      hearings,
      lat: offset.lat,
      lng: offset.lng,
      hasGps: true,
      band,
      channel,
      rssiDbm: Number(alert.strongest_rssi_dbm ?? strongest.rssi_dbm),
      receivedAt,
      surveySource: strongest.survey_source || '',
      isFresh: isSurveyFresh(receivedAt, nowMs, EVIL_TWIN_SURVEY_MAX_AGE_MS),
      isCap: false,
      chLoad: 0,
      bss: 0,
      maxRssi: Number(alert.strongest_rssi_dbm ?? strongest.rssi_dbm),
      noiseFloor: null,
    });
  });

  return {
    markers: attachLabelPositions(markers),
    unplottable,
  };
}

function distinctHearerSerials(hearings) {
  return [...new Set(
    hearings
      .map((h) => String(h?.hearer_serial || '').toLowerCase())
      .filter(Boolean),
  )];
}

function formatMacColon(key) {
  if (key.length !== 12) return key;
  return key.match(/.{2}/g).join(':');
}

function buildMarker(device, radio, entry, extras = {}) {
  const serial = device.serial_number;
  const band = normalizeBand(radio.band);
  const radioLabel = String(radio.label || 'radio').toLowerCase();
  const gps = getDeviceGps(device);
  const minRssiDbm = extras.minRssiDbm ?? DEFAULT_ACS_RSSI_FLOOR_DBM;
  const filteredNeighbours = filterNeighboursByMinRssi(
    entry.neighbours,
    entry.noiseFloor,
    minRssiDbm,
  );

  return {
    id: `${serial.toLowerCase()}|${radioLabel}|${band}|${entry.channel}`,
    serial,
    serialSuffix: String(serial).slice(-4),
    lat: gps?.lat ?? null,
    lng: gps?.lng ?? null,
    hasGps: Boolean(gps),
    band,
    radioLabel: radio.label || '',
    channel: entry.channel,
    frequencyMHz: entry.frequency || null,
    maxRssi: entry.maxRssi || null,
    minRssi: entry.minRssi || null,
    noiseFloor: entry.noiseFloor || null,
    chLoad: entry.chLoad || 0,
    bss: entry.bss || 0,
    neighbours: filteredNeighbours,
    neighbourCountTotal: (entry.neighbours || []).length,
    receivedAt: device.acs_survey?.received_at || null,
    surveySource: device.acs_survey?.source || '',
    isFresh: extras.isFresh !== false,
    isCap: extras.isCap === true,
    isForeign: false,
    interferenceScore: channelInterferenceScore(entry),
  };
}

/**
 * Flatten ACS surveys into map markers.
 * Map markers use the busiest scanned channel per radio. The min-RSSI floor
 * filters neighbour BSSID rows (SSIDs) in the marker detail, not which channel
 * is selected for the map pin.
 */
export function buildChannelSurveyMarkers(devices, options = {}) {
  const minRssiDbm = Number.isFinite(Number(options.minRssiDbm))
    ? Number(options.minRssiDbm)
    : DEFAULT_ACS_RSSI_FLOOR_DBM;
  const bandFilter = options.bandFilter || 'all';
  const selectedChannels = Array.isArray(options.selectedChannels) && options.selectedChannels.length > 0
    ? new Set(options.selectedChannels.map((c) => Number(c)).filter((c) => c > 0))
    : null;
  const nowMs = Number(options.nowMs) || Date.now();
  const markerSource = normalizeSurveyMarkerSourceFilter(options.markerSourceFilter);
  const showJuganu = markerSource === SURVEY_MARKER_SOURCE.ALL
    || markerSource === SURVEY_MARKER_SOURCE.JUGANU;
  const showExternal = markerSource === SURVEY_MARKER_SOURCE.ALL
    || markerSource === SURVEY_MARKER_SOURCE.EXTERNAL;

  const ownMarkers = [];
  let devicesWithSurvey = 0;
  let devicesWithFreshSurvey = 0;
  let devicesWithGps = 0;

  if (showJuganu) {
    (devices || []).forEach((device) => {
      if (!isSurveyEligibleDevice(device)) return;

      const survey = device?.acs_survey;
      if (!survey?.radios?.length) return;
      devicesWithSurvey += 1;

      const fresh = isSurveyFresh(survey.received_at, nowMs);
      if (fresh) devicesWithFreshSurvey += 1;

      const gps = getDeviceGps(device);
      if (gps) devicesWithGps += 1;

      const capType = getDeviceType(device?.capabilities) === 'CAP';

      survey.radios.forEach((radio) => {
        const band = normalizeBand(radio?.band);
        if (!isBackhaulSurveyBand(band)) return;
        if (bandFilter !== 'all' && band !== bandFilter) return;

        const entries = (radio.channels || [])
          .map(normalizeChannelEntry)
          .filter(Boolean);

        if (selectedChannels) {
          entries
            .filter((entry) => selectedChannels.has(entry.channel))
            .forEach((entry) => {
              ownMarkers.push(buildMarker(device, radio, entry, {
                isFresh: fresh,
                isCap: capType,
                minRssiDbm,
              }));
            });
          return;
        }

        if (entries.length === 0) return;

        let worst = entries[0];
        entries.forEach((entry) => {
          if (channelInterferenceScore(entry) > channelInterferenceScore(worst)) {
            worst = entry;
          }
        });
        ownMarkers.push(buildMarker(device, radio, worst, {
          isFresh: fresh,
          isCap: capType,
          minRssiDbm,
        }));
      });
    });
  }

  const markerCountBeforeGps = ownMarkers.length;
  const macIndex = buildDeviceMacIndex(devices);
  const foreignMarkers = showExternal
    ? buildForeignHeardApMarkers(devices, options, macIndex)
    : [];
  const plottableOwn = ownMarkers.filter((m) => m.hasGps && m.lat != null && m.lng != null);
  const plottableForeign = foreignMarkers.filter((m) => m.hasGps && m.lat != null && m.lng != null);
  const plottable = [...plottableOwn, ...plottableForeign];

  return {
    markers: attachLabelPositions(plottable),
    devicesWithSurvey,
    devicesWithFreshSurvey,
    devicesWithGps,
    markerCountBeforeGps,
    mapPlottableCount: plottable.length,
    foreignApCount: plottableForeign.length,
    ownDeviceMarkerCount: plottableOwn.length,
    markerSourceFilter: markerSource,
  };
}

export function summarizeChannelSurvey(devices) {
  const nowMs = Date.now();
  let withSurvey = 0;
  let fresh = 0;
  let stale = 0;
  (devices || []).forEach((d) => {
    if (!isSurveyEligibleDevice(d)) return;
    if (!d?.acs_survey?.radios?.length) return;
    withSurvey += 1;
    if (isSurveyFresh(d.acs_survey.received_at, nowMs)) fresh += 1;
    else stale += 1;
  });
  return { withSurvey, fresh, stale };
}

export function describeEmptyChannelSurvey({
  loading,
  error,
  devicesWithSurvey,
  markerCountBeforeGps = 0,
  mapPlottableCount,
  hasChannelFilter = false,
  markerSourceFilter = SURVEY_MARKER_SOURCE.ALL,
}) {
  if (loading) return null;
  if (error) return error;
  if (devicesWithSurvey === 0 && markerSourceFilter !== SURVEY_MARKER_SOURCE.EXTERNAL) {
    return 'No ACS surveys on connected jnet2 devices. Surveys are captured at device boot (ACS_report).';
  }
  if (markerCountBeforeGps > 0 && mapPlottableCount === 0) {
    return 'ACS surveys found but no GPS coordinates — add device GPS or gps_override to plot the map.';
  }
  if (mapPlottableCount === 0 && markerSourceFilter === SURVEY_MARKER_SOURCE.EXTERNAL) {
    return 'No external APs heard at or above the min-signal floor for the current band/channel selection.';
  }
  if (mapPlottableCount === 0 && markerSourceFilter === SURVEY_MARKER_SOURCE.JUGANU) {
    return 'No Juganu survey points to plot for the current filters.';
  }
  if (mapPlottableCount === 0 && hasChannelFilter) {
    return 'No surveyed channels match the current band or channel selection.';
  }
  if (mapPlottableCount === 0) {
    return 'No survey points to plot for the current filters.';
  }
  return null;
}
